import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
  UsersIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  NewspaperIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import conduttoreIcon from "../assets/icons/Service-Leader-Icon.png";
import { toastError, toastSuccess } from "../utils/toast";
import CalendarSyncCard from "../components/CalendarSyncCard";
import PublicTalksUpcomingCard from "../components/PublicTalksUpcomingCard";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DASHBOARD_NEWS_PAGE_SIZE = 3;
const DASHBOARD_TURNI_LOOKAHEAD_DAYS = 60;
const BACHECA_MAX_FORWARD_DAYS = 60;
const BACHECA_TAB_KEYS = Object.freeze({
  EVENTI: "eventi",
  TURNI_POSTAZIONI: "turni_postazioni",
});
const DASHBOARD_SECTION_KEYS = Object.freeze({
  MIEI_PROSSIMI_TURNI: "miei_prossimi_turni",
  INCARICHI: "incarichi",
  PROSSIME_ADUNANZE: "prossime_adunanze_da_condurre",
  BACHECA: "bacheca",
  NOTIFICHE_RECENTI: "notifiche_recenti",
});
const DEFAULT_DASHBOARD_SECTIONS_CONFIG = Object.freeze({
  [DASHBOARD_SECTION_KEYS.MIEI_PROSSIMI_TURNI]: true,
  [DASHBOARD_SECTION_KEYS.INCARICHI]: true,
  [DASHBOARD_SECTION_KEYS.PROSSIME_ADUNANZE]: true,
  [DASHBOARD_SECTION_KEYS.BACHECA]: true,
  [DASHBOARD_SECTION_KEYS.NOTIFICHE_RECENTI]: true,
});

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }
  const date =
    value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateParam = (date) => {
  const normalized = normalizeDate(date);
  if (!normalized) {
    return "";
  }
  const tzOffset = normalized.getTimezoneOffset() * 60000;
  return new Date(normalized.getTime() - tzOffset).toISOString().split("T")[0];
};

const getWeekStartMonday = (value) => {
  const date = normalizeDate(value);
  if (!date) {
    return null;
  }
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  return normalizeDate(new Date(date.getTime() + diffToMonday * DAY_IN_MS));
};

const getWeekEndSunday = (value) => {
  const weekStart = getWeekStartMonday(value);
  if (!weekStart) {
    return null;
  }
  return normalizeDate(new Date(weekStart.getTime() + 6 * DAY_IN_MS));
};

const formatAdunanzaDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const formatter = new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const capitalize = (text) =>
    text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

  return formatter
    .formatToParts(date)
    .map((part) => {
      if (part.type === "weekday" || part.type === "month") {
        return capitalize(part.value);
      }
      return part.value;
    })
    .join("");
};

const formatPhone = (phone) => {
  if (!phone) return "";
  const digitsOnly = String(phone).replace(/\D/g, "");
  if (digitsOnly.length === 10 && digitsOnly.startsWith("3")) {
    return `${digitsOnly.substring(0, 3)} ${digitsOnly.substring(3, 6)} ${digitsOnly.substring(6, 8)} ${digitsOnly.substring(8)}`;
  }
  return String(phone).trim();
};

const buildPhoneHref = (phone) => {
  if (!phone) return "";
  const trimmed = String(phone).trim();
  const normalized = trimmed.replace(/[^\d+]/g, "");
  return normalized ? `tel:${normalized}` : "";
};

const normalizeDashboardSectionsConfig = (value) => {
  const normalized = { ...DEFAULT_DASHBOARD_SECTIONS_CONFIG };
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return normalized;
  }

  Object.values(DASHBOARD_SECTION_KEYS).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      normalized[key] = Boolean(value[key]);
    }
  });

  return normalized;
};

const Dashboard = () => {
  const { user, activeCongregazione } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalVolontari: 0,
    postazioniAttive: 0,
    turniSettimana: 0,
    turniIncompleti: 0,
    prossimiTurni: [],
    prossimeAdunanze: [],
    notifiche: [],
    incarichiPulizie: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsItems, setNewsItems] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsPage, setNewsPage] = useState(1);
  const [newsPagination, setNewsPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [newsError, setNewsError] = useState(null);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const isAdmin =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const conduttoreName = [user?.nome, user?.cognome]
    .filter(Boolean)
    .join(" ");
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [turnDetails, setTurnDetails] = useState({
    open: false,
    loading: false,
    error: null,
    turns: [],
    selectedTurnId: null,
    meta: null,
  });
  const [bachecaDayOffset, setBachecaDayOffset] = useState(0);
  const [dailyPostazioniTurni, setDailyPostazioniTurni] = useState([]);
  const [dailyPostazioniLoading, setDailyPostazioniLoading] = useState(false);
  const [dailyPostazioniError, setDailyPostazioniError] = useState(null);
  const [bachecaEvents, setBachecaEvents] = useState([]);
  const [bachecaEventsLoading, setBachecaEventsLoading] = useState(false);
  const [bachecaEventsError, setBachecaEventsError] = useState(null);
  const [dashboardSectionsConfig, setDashboardSectionsConfig] = useState(
    DEFAULT_DASHBOARD_SECTIONS_CONFIG
  );
  const [dashboardSectionsLoading, setDashboardSectionsLoading] =
    useState(false);
  const [dashboardSectionsError, setDashboardSectionsError] = useState(null);
  const [bachecaActiveTab, setBachecaActiveTab] = useState(
    BACHECA_TAB_KEYS.TURNI_POSTAZIONI
  );
  const [dashboardSettingsOpen, setDashboardSettingsOpen] = useState(false);
  const [dashboardSettingsSaving, setDashboardSettingsSaving] = useState(false);
  const [dashboardSettingsDraft, setDashboardSettingsDraft] = useState(
    DEFAULT_DASHBOARD_SECTIONS_CONFIG
  );

  useEffect(() => {
    const fetchStats = async () => {
      // Non fare nulla se l'utente non è ancora caricato
      if (!user?.id) {
        return;
      }

      // Se la congregazione attiva è cambiata, aspetta un momento per permettere l'aggiornamento del localStorage
      if (activeCongregazione) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      try {
        setLoading(true);
        setError(null);

        // Calcola le date per la settimana corrente e i prossimi 60 giorni
        const oggi = normalizeDate(new Date());
        const inizioSettimana = getWeekStartMonday(oggi);
        const fineSettimana = getWeekEndSunday(oggi);
        const dataInizio = formatDateParam(inizioSettimana);
        const dataFine = formatDateParam(
          new Date(oggi.getTime() + DASHBOARD_TURNI_LOOKAHEAD_DAYS * DAY_IN_MS)
        );

        // Carica i dati reali dal backend
        // Gestisci l'API dei turni separatamente perché potrebbe fallire se non ci sono turni
        const conduttoreRequests =
          user?.sesso === "M" && user?.service_leader
            ? [
                api
                  .get("/adunanze-servizio/miei-turni?limit=5")
                  .catch((err) => {
                    console.warn("Errore caricamento adunanze conduttore:", err);
                    return { data: { assignments: [] } };
                  }),
              ]
            : [];

        const [volontariRes, postazioniRes, turniRes, pulizieRes, ...rest] =
          await Promise.allSettled([
            api.get("/volontari"),
            api.get("/postazioni?stato=attiva"),
            api
              .get(
                `/turni/miei-turni?data_inizio=${dataInizio}&data_fine=${dataFine}`
              )
              .catch((err) => {
                // Se l'API dei turni fallisce, restituisci array vuoto
                console.warn("Errore nel caricamento turni:", err);
                return { data: [] };
              }),
            api
              .get(
                `/turni-pulizie/miei-turni?data_inizio=${dataInizio}&data_fine=${dataFine}`
              )
              .catch((err) => {
                console.warn("Errore nel caricamento turni pulizie:", err);
                return { data: { assignments: [] } };
              }),
            ...conduttoreRequests,
          ]);

        // Calcola le statistiche dai dati reali
        const volontariData = volontariRes.status === 'fulfilled' 
          ? volontariRes.value.data 
          : { volontari: [], pagination: { total: 0 } };
        
        const postazioniData = postazioniRes.status === 'fulfilled'
          ? postazioniRes.value.data
          : [];
        
        const turniData = turniRes.status === 'fulfilled'
          ? turniRes.value.data
          : [];

        const prossimeAdunanzeData =
          rest.length > 0 && rest[0]?.status === "fulfilled"
            ? rest[0].value.data?.assignments || []
            : [];

        const incarichiPulizie =
          pulizieRes.status === "fulfilled"
            ? pulizieRes.value.data?.assignments || []
            : [];

        const totalVolontari =
          volontariData.pagination?.total ||
          volontariData.volontari?.length ||
          (Array.isArray(volontariData) ? volontariData.length : 0);
        
        const postazioniAttive = Array.isArray(postazioniData)
          ? postazioniData.length
          : postazioniData.total || 0;

        // Processa i turni assegnati all'utente
        const turniMappati = Array.isArray(turniData)
          ? turniData.map((turno) => {
              const compagni =
                Array.isArray(turno.compagni) && turno.compagni.length > 0
                  ? turno.compagni
                      .map((c) =>
                        [c.nome, c.cognome].filter(Boolean).join(" ")
                      )
                      .filter(Boolean)
                  : turno.volontari_nomi
                  ? turno.volontari_nomi.split(", ").filter(Boolean)
                  : Array.isArray(turno.volontari)
                  ? turno.volontari
                  : [];

              const orarioInizio = turno.orario?.inizio || turno.orario_inizio;
              const orarioFine = turno.orario?.fine || turno.orario_fine;
              const formatTimePart = (value) =>
                value ? value.substring(0, 5) : "";
              const fallbackOrario =
                typeof turno.orario === "string" ? turno.orario : "";
              const orarioFormattato =
                [formatTimePart(orarioInizio), formatTimePart(orarioFine)]
                  .filter(Boolean)
                  .join(" - ") || fallbackOrario;

              return {
                id: turno.id || turno.assegnazione_id,
                luogo: turno.postazione?.luogo || turno.luogo || "Postazione",
                indirizzo: turno.postazione?.indirizzo || turno.indirizzo || "",
                data: turno.data_turno || turno.data,
                orario: orarioFormattato,
                volontari: compagni,
                stato: turno.stato,
              };
            })
          : [];

        const prossimiTurni = turniMappati
          .filter((turno) => {
            const turnoDate = normalizeDate(turno.data);
            return turnoDate && oggi && turnoDate >= oggi;
          })
          .sort((a, b) => {
            const aDate = normalizeDate(a.data);
            const bDate = normalizeDate(b.data);
            return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
          });

        const newStats = {
          totalVolontari,
          postazioniAttive,
          turniSettimana: turniMappati.filter((t) => {
            const turnoDate = normalizeDate(t.data);
            if (!turnoDate || !inizioSettimana || !fineSettimana) {
              return false;
            }
            return turnoDate >= inizioSettimana && turnoDate <= fineSettimana;
          }).length,
          turniIncompleti: 0, // TODO: implementare quando avremo l'API dei turni
          prossimiTurni,
          prossimeAdunanze: prossimeAdunanzeData,
          incarichiPulizie,
          notifiche: [], // TODO: implementare quando avremo l'API delle notifiche
        };

        setStats(newStats);
      } catch (err) {
        console.error("Errore nel caricamento delle statistiche:", err);
        console.error("Dettagli errore:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        });

        // Se è un errore di autenticazione, non mostrare errore (verrà gestito dall'AuthContext)
        if (err.response?.status === 401) {
          return;
        }

        // Mostra messaggio di errore più dettagliato
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Errore nel caricamento delle statistiche";
        setError(errorMessage);

        // Fallback con dati di base
        setStats({
          totalVolontari: 0,
          postazioniAttive: 0,
          turniSettimana: 0,
          turniIncompleti: 0,
          prossimiTurni: [],
          prossimeAdunanze: [],
          incarichiPulizie: [],
          notifiche: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.id, activeCongregazione?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setNewsPage(1);
  }, [user?.id]);

  useEffect(() => {
    const fetchDashboardSectionsConfig = async () => {
      if (!user?.id || !activeCongregazione?.id) {
        const fallback = { ...DEFAULT_DASHBOARD_SECTIONS_CONFIG };
        setDashboardSectionsConfig(fallback);
        setDashboardSettingsDraft(fallback);
        setDashboardSectionsError(null);
        setDashboardSectionsLoading(false);
        return;
      }

      try {
        setDashboardSectionsLoading(true);
        setDashboardSectionsError(null);
        const response = await api.get("/bacheca/dashboard-sections-config");
        const nextConfig = normalizeDashboardSectionsConfig(
          response.data?.sections_config
        );
        setDashboardSectionsConfig(nextConfig);
        setDashboardSettingsDraft(nextConfig);
      } catch (fetchError) {
        console.error(
          "Errore nel caricamento configurazione sezioni dashboard:",
          fetchError
        );
        const fallback = { ...DEFAULT_DASHBOARD_SECTIONS_CONFIG };
        setDashboardSectionsConfig(fallback);
        setDashboardSettingsDraft(fallback);
        setDashboardSectionsError(
          fetchError.response?.data?.message ||
            "Impossibile caricare le impostazioni della dashboard"
        );
      } finally {
        setDashboardSectionsLoading(false);
      }
    };

    fetchDashboardSectionsConfig();
  }, [user?.id, activeCongregazione?.id]);

  useEffect(() => {
    if (!user?.id || !activeCongregazione?.id) {
      setBachecaEvents([]);
      setBachecaEventsLoading(false);
      setBachecaEventsError(null);
      setExpandedEventId(null);
      return;
    }

    const fetchBachecaEvents = async () => {
      try {
        setBachecaEventsLoading(true);
        setBachecaEventsError(null);
        const response = await api.get("/eventi?bacheca=true&limit=12");
        setBachecaEvents(Array.isArray(response.data?.eventi) ? response.data.eventi : []);
      } catch (fetchError) {
        console.error("Errore nel caricamento eventi bacheca:", fetchError);
        setBachecaEvents([]);
        setBachecaEventsError(
          fetchError.response?.data?.message ||
            "Impossibile caricare gli eventi in bacheca"
        );
      } finally {
        setBachecaEventsLoading(false);
      }
    };

    fetchBachecaEvents();
  }, [user?.id, activeCongregazione?.id]);

  useEffect(() => {
    const fetchDailyPostazioniTurni = async () => {
      if (!user?.id || !activeCongregazione?.id) {
        setDailyPostazioniTurni([]);
        setDailyPostazioniError(null);
        setDailyPostazioniLoading(false);
        return;
      }

      const today = normalizeDate(new Date());
      if (!today) {
        setDailyPostazioniTurni([]);
        setDailyPostazioniError(null);
        setDailyPostazioniLoading(false);
        return;
      }

      const targetDate = new Date(today.getTime() + bachecaDayOffset * DAY_IN_MS);
      const targetDateIso = formatDateParam(targetDate);
      if (!targetDateIso) {
        setDailyPostazioniTurni([]);
        setDailyPostazioniError(null);
        setDailyPostazioniLoading(false);
        return;
      }

      try {
        setDailyPostazioniLoading(true);
        setDailyPostazioniError(null);

        const response = await api.get(
          `/turni?data_inizio=${targetDateIso}&data_fine=${targetDateIso}`
        );
        const rawTurni = Array.isArray(response.data) ? response.data : [];
        const groupedMap = new Map();

        rawTurni.forEach((turno) => {
          const stato = String(turno?.stato || "").toLowerCase();
          if (!["attivo", "assegnato", "completato"].includes(stato)) {
            return;
          }

          const volunteers = String(turno?.volontari_nomi || "")
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean);
          if (volunteers.length === 0) {
            return;
          }

          const postazioneIdRaw = turno?.postazione_id ?? turno?.postazioneId;
          const parsedPostazioneId = parseInt(postazioneIdRaw, 10);
          const fallbackKey = `${turno?.luogo || "postazione"}-${turno?.indirizzo || ""}`;
          const postazioneKey = Number.isNaN(parsedPostazioneId)
            ? fallbackKey
            : String(parsedPostazioneId);

          if (!groupedMap.has(postazioneKey)) {
            groupedMap.set(postazioneKey, {
              id: Number.isNaN(parsedPostazioneId) ? null : parsedPostazioneId,
              luogo: turno?.luogo || "Postazione",
              indirizzo: turno?.indirizzo || "",
              slotsMap: new Map(),
            });
          }

          const entry = groupedMap.get(postazioneKey);
          const slotIdRaw = turno?.slot_orario_id ?? turno?.slotOrarioId;
          const parsedSlotId = parseInt(slotIdRaw, 10);
          const orarioInizio = formatTimeShort(turno?.orario_inizio || "");
          const orarioFine = formatTimeShort(turno?.orario_fine || "");
          const slotKey = Number.isNaN(parsedSlotId)
            ? `${orarioInizio}-${orarioFine}`
            : String(parsedSlotId);

          if (!entry.slotsMap.has(slotKey)) {
            entry.slotsMap.set(slotKey, {
              id: Number.isNaN(parsedSlotId) ? null : parsedSlotId,
              orarioInizio,
              orarioFine,
              volontariSet: new Set(),
            });
          }

          const slotEntry = entry.slotsMap.get(slotKey);
          volunteers.forEach((name) => slotEntry.volontariSet.add(name));
        });

        const grouped = Array.from(groupedMap.values())
          .map((entry) => ({
            id: entry.id,
            luogo: entry.luogo,
            indirizzo: entry.indirizzo,
            slots: Array.from(entry.slotsMap.values())
              .map((slotEntry) => ({
                id: slotEntry.id,
                orarioInizio: slotEntry.orarioInizio,
                orarioFine: slotEntry.orarioFine,
                volontari: Array.from(slotEntry.volontariSet).sort((a, b) =>
                  a.localeCompare(b, "it")
                ),
              }))
              .filter((slotEntry) => slotEntry.volontari.length > 0)
              .sort((a, b) =>
                `${a.orarioInizio}`.localeCompare(`${b.orarioInizio}`, "it")
              ),
          }))
          .filter((entry) => entry.slots.length > 0)
          .sort((a, b) => a.luogo.localeCompare(b.luogo, "it"));

        setDailyPostazioniTurni(grouped);
      } catch (fetchError) {
        console.error(
          "Errore nel caricamento dei turni giornalieri per la bacheca:",
          fetchError
        );
        setDailyPostazioniTurni([]);
        setDailyPostazioniError(
          fetchError.response?.data?.message ||
            "Impossibile caricare i turni giornalieri"
        );
      } finally {
        setDailyPostazioniLoading(false);
      }
    };

    fetchDailyPostazioniTurni();
  }, [
    user?.id,
    activeCongregazione?.id,
    bachecaDayOffset,
  ]);

  const fetchDashboardNews = useCallback(
    async (pageToLoad = 1) => {
      if (!user?.id) {
        return;
      }
      setNewsLoading(true);
      setNewsError(null);
      try {
        const response = await api.get(
          `/news?page=${pageToLoad}&limit=${DASHBOARD_NEWS_PAGE_SIZE}`
        );
        setNewsItems(response.data?.news || []);
        const pagination = response.data?.pagination || {};
        setNewsPagination({
          total: pagination.total || 0,
          totalPages: pagination.totalPages || 0,
        });
      } catch (fetchError) {
        console.error("Errore nel caricamento delle news dashboard:", fetchError);
        setNewsItems([]);
        setNewsError("Impossibile caricare le news al momento");
      } finally {
        setNewsLoading(false);
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (user?.id) {
      fetchDashboardNews(newsPage);
    }
  }, [fetchDashboardNews, newsPage, user?.id]);

  const formatTimeShort = (value) => {
    if (!value) return "";
    return value.substring(0, 5);
  };

  const closeTurnDetails = () => {
    setTurnDetails({
      open: false,
      loading: false,
      error: null,
      turns: [],
      selectedTurnId: null,
      meta: null,
    });
  };

  const openTurnDetails = async (turno) => {
    if (!turno?.id) return;

    setTurnDetails({
      open: true,
      loading: true,
      error: null,
      turns: [],
      selectedTurnId: turno.id,
      meta: {
        luogo: turno.luogo,
        data: turno.data,
        orario: turno.orario,
      },
    });

    try {
      const detailRes = await api.get(`/turni/${turno.id}`);
      const detail = detailRes.data || {};
      const rawDate = detail.data_turno || turno.data;
      const dateKey = rawDate
        ? typeof rawDate === "string"
          ? rawDate.split("T")[0]
          : formatDateParam(rawDate)
        : "";
      const postazioneId =
        detail.postazione_id || detail.postazioneId || detail.postazione?.id;

      if (!postazioneId || !dateKey) {
        throw new Error(
          "Impossibile recuperare la postazione o la data del turno"
        );
      }

      const dayRes = await api.get(
        `/turni?postazione_id=${postazioneId}&data_inizio=${dateKey}&data_fine=${dateKey}`
      );
      const dayTurns = Array.isArray(dayRes.data) ? dayRes.data : [];

      const mapped = dayTurns
        .map((t) => ({
          id: t.id,
          orarioInizio: formatTimeShort(t.orario_inizio || t.orario?.inizio),
          orarioFine: formatTimeShort(t.orario_fine || t.orario?.fine),
          volontari: Array.isArray(t.volontari_dettagli)
            ? t.volontari_dettagli
                .map((volontario, index) => {
                  const nomeCompleto =
                    volontario?.nome_completo ||
                    [volontario?.nome, volontario?.cognome]
                      .filter(Boolean)
                      .join(" ")
                      .trim();
                  return {
                    id: volontario?.id || `${t.id}_${index}`,
                    nomeCompleto: nomeCompleto || "Volontario",
                    telefono: volontario?.telefono || "",
                  };
                })
                .sort((a, b) =>
                  a.nomeCompleto.localeCompare(b.nomeCompleto, "it")
                )
            : t.volontari_nomi
            ? t.volontari_nomi
                .split(", ")
                .filter(Boolean)
                .map((nome, index) => ({
                  id: `${t.id}_${index}`,
                  nomeCompleto: nome,
                  telefono: "",
                }))
            : Array.isArray(t.volontari)
            ? t.volontari.map((nome, index) => ({
                id: `${t.id}_${index}`,
                nomeCompleto: nome,
                telefono: "",
              }))
            : [],
          stato: t.stato,
          luogo: t.luogo,
        }))
        .sort((a, b) =>
          (a.orarioInizio || "").localeCompare(b.orarioInizio || "")
        );

      setTurnDetails({
        open: true,
        loading: false,
        error: null,
        turns: mapped,
        selectedTurnId: detail.id || turno.id,
        meta: {
          luogo: detail.luogo || turno.luogo,
          data: dateKey,
          orario: turno.orario,
        },
      });
    } catch (err) {
      setTurnDetails((prev) => ({
        ...prev,
        loading: false,
        error:
          err.response?.data?.message ||
          err.message ||
          "Errore nel caricamento dei turni della giornata",
      }));
    }
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color = "blue",
    onClick,
    href,
  }) => {
    const isInteractive = Boolean(onClick || href);
    const baseClasses =
      "bg-white rounded-lg shadow-sm border border-gray-200 p-6";
    const interactiveClasses = isInteractive
      ? "transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm cursor-pointer"
      : "";
    const content = (
      <div className="flex items-center">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    );

    if (href) {
      return (
        <a
          href={href}
          className={`${baseClasses} ${interactiveClasses} block`}
          aria-label={title}
        >
          {content}
        </a>
      );
    }

    if (onClick) {
      return (
        <button
          type="button"
          onClick={onClick}
          className={`${baseClasses} ${interactiveClasses} w-full text-left`}
          aria-label={title}
        >
          {content}
        </button>
      );
    }

    return <div className={baseClasses}>{content}</div>;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "Data non disponibile";
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "Data non disponibile";
    }
    return date.toLocaleDateString("it-IT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const capitalizeFirst = (word = "") =>
    word ? word.charAt(0).toUpperCase() + word.slice(1) : "";

  const formatWeekRange = (startIso, endIso) => {
    if (!startIso || !endIso) return "";
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "";
    }
    const formatter = new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "long",
    });
    const capitalizeMonth = (str) => {
      if (!str) return "";
      const parts = str.split(" ");
      if (parts.length === 2) {
        const month = parts[1];
        parts[1] = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
        return parts.join(" ");
      }
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    const startStr = capitalizeMonth(formatter.format(start));
    const endStr = capitalizeMonth(formatter.format(end));
    return `${startStr} - ${endStr}`;
  };

  const formatRange = (start, end) => {
    if (!start) return "";
    const formatEventDate = (value) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      const str = date.toLocaleDateString("it-IT", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const parts = str.split(" ");
      if (parts.length >= 4) {
        parts[0] = capitalizeFirst(parts[0]);
        parts[2] = capitalizeFirst(parts[2]);
        return parts.join(" ");
      }
      return capitalizeFirst(str);
    };

    const startFmt = formatEventDate(start);
    const endFmt = end && end !== start ? formatEventDate(end) : "";
    return endFmt ? `${startFmt} - ${endFmt}` : startFmt;
  };

  const formatNewsDate = (value) => {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const summarizeNewsContent = (text, maxLength = 220) => {
    if (!text) return "";
    const normalized = text.trim();
    if (normalized.length <= maxLength) {
      return normalized;
    }
    return `${normalized.slice(0, maxLength).trim()}…`;
  };

  const derivedNewsTotalPages = useMemo(() => {
    if (newsPagination.totalPages && newsPagination.totalPages > 0) {
      return newsPagination.totalPages;
    }
    if (newsPagination.total && newsPagination.total > 0) {
      return Math.max(
        1,
        Math.ceil(newsPagination.total / DASHBOARD_NEWS_PAGE_SIZE)
      );
    }
    return 0;
  }, [newsPagination.totalPages, newsPagination.total]);

  const canGoPrev = newsPage > 1;
  const canGoNext =
    derivedNewsTotalPages === 0
      ? newsItems.length === DASHBOARD_NEWS_PAGE_SIZE
      : newsPage < derivedNewsTotalPages;

  const totalNewsCount =
    (newsPagination?.total || 0) > 0 ? newsPagination.total : newsItems.length;

  const incarichiPulizieGrouped = useMemo(() => {
    const map = new Map();
    (stats.incarichiPulizie || []).forEach((inc) => {
      const key = inc.settimana || inc.settimana_fine || `${inc.slot}_${inc.group_id}`;
      if (!map.has(key)) {
        map.set(key, {
          settimana: inc.settimana,
          settimana_fine: inc.settimana_fine,
          items: [],
        });
      }
      map.get(key).items.push({
        slot: inc.slot,
        groups: inc.groups || (inc.group_nome || inc.group_id
          ? [{ group_id: inc.group_id, group_nome: inc.group_nome }]
          : []),
      });
    });
    return Array.from(map.values()).sort((a, b) => {
      const ad = normalizeDate(a.settimana);
      const bd = normalizeDate(b.settimana);
      return (ad?.getTime() || 0) - (bd?.getTime() || 0);
    });
  }, [stats.incarichiPulizie]);

  const displayTotalPages =
    derivedNewsTotalPages > 0
      ? derivedNewsTotalPages
      : newsItems.length > 0
      ? Math.max(1, newsPage)
      : 0;
  const displayCurrentPage =
    displayTotalPages > 0 ? Math.min(newsPage, displayTotalPages) : 0;

  useEffect(() => {
    if (
      derivedNewsTotalPages > 0 &&
      newsPage > derivedNewsTotalPages
    ) {
      setNewsPage(derivedNewsTotalPages);
    }
  }, [derivedNewsTotalPages, newsPage]);

  const welcomeLabel = user?.sesso === "F" ? "Benvenuta" : "Benvenuto";
  const bachecaSelectedDate = useMemo(() => {
    const today = normalizeDate(new Date());
    if (!today) return null;
    return new Date(today.getTime() + bachecaDayOffset * DAY_IN_MS);
  }, [bachecaDayOffset]);
  const canManageDashboardSections =
    Boolean(activeCongregazione?.id) && isAdmin;
  const bachecaTabs = useMemo(
    () => [
      { key: BACHECA_TAB_KEYS.EVENTI, label: "Eventi" },
      {
        key: BACHECA_TAB_KEYS.TURNI_POSTAZIONI,
        label: "Calendario Giornaliero Turni",
      },
    ],
    []
  );
  const dashboardSectionOptions = useMemo(
    () => [
      {
        key: DASHBOARD_SECTION_KEYS.MIEI_PROSSIMI_TURNI,
        label: "I Miei Prossimi Turni",
      },
      { key: DASHBOARD_SECTION_KEYS.INCARICHI, label: "Incarichi" },
      {
        key: DASHBOARD_SECTION_KEYS.PROSSIME_ADUNANZE,
        label: "Prossime Adunanze da condurre",
      },
      { key: DASHBOARD_SECTION_KEYS.BACHECA, label: "Bacheca" },
      {
        key: DASHBOARD_SECTION_KEYS.NOTIFICHE_RECENTI,
        label: "Notifiche Recenti",
      },
    ],
    []
  );
  const visibleSections = normalizeDashboardSectionsConfig(
    dashboardSectionsConfig
  );
  const showMieiProssimiTurni = visibleSections.miei_prossimi_turni;
  const showIncarichi = visibleSections.incarichi;
  const showBacheca = visibleSections.bacheca;
  const showNotificheRecenti = visibleSections.notifiche_recenti;
  const showProssimeAdunanze =
    visibleSections.prossime_adunanze_da_condurre &&
    user?.sesso === "M" &&
    user?.service_leader;

  const hasAtLeastOneSectionEnabled = useMemo(
    () =>
      Object.values(
        normalizeDashboardSectionsConfig(dashboardSettingsDraft)
      ).some(Boolean),
    [dashboardSettingsDraft]
  );

  const openDashboardSettings = () => {
    setDashboardSettingsDraft(
      normalizeDashboardSectionsConfig(dashboardSectionsConfig)
    );
    setDashboardSettingsOpen(true);
  };

  const toggleDashboardSection = (key) => {
    setDashboardSettingsDraft((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const saveDashboardSectionsSettings = async () => {
    const nextConfig = normalizeDashboardSectionsConfig(dashboardSettingsDraft);
    if (!Object.values(nextConfig).some(Boolean)) {
      toastError("Mantieni almeno una sezione della dashboard attiva");
      return;
    }

    try {
      setDashboardSettingsSaving(true);
      const response = await api.patch(
        "/bacheca/dashboard-sections-config",
        nextConfig
      );
      const updatedConfig = normalizeDashboardSectionsConfig(
        response.data?.sections_config || nextConfig
      );
      setDashboardSectionsConfig(updatedConfig);
      setDashboardSettingsDraft(updatedConfig);
      setDashboardSettingsOpen(false);
      toastSuccess("Impostazioni dashboard aggiornate");
    } catch (saveError) {
      console.error("Errore salvataggio impostazioni dashboard:", saveError);
      toastError(
        saveError.response?.data?.message ||
          "Impossibile salvare le impostazioni della dashboard"
      );
    } finally {
      setDashboardSettingsSaving(false);
    }
  };

  useEffect(() => {
    if (bachecaTabs.some((tab) => tab.key === bachecaActiveTab)) return;
    setBachecaActiveTab(BACHECA_TAB_KEYS.TURNI_POSTAZIONI);
  }, [bachecaTabs, bachecaActiveTab]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Caricamento statistiche...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Errore</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {welcomeLabel}, {user?.nome} {user?.cognome}
            </h1>
            <p className="text-gray-600 mt-1">
              Ecco un riepilogo delle attività del sistema di gestione turni
            </p>
          </div>
          {canManageDashboardSections && (
            <button
              type="button"
              onClick={openDashboardSettings}
              disabled={dashboardSectionsLoading}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Impostazioni sezioni dashboard"
              aria-label="Impostazioni sezioni dashboard"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        {dashboardSectionsError && (
          <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            {dashboardSectionsError}
          </div>
        )}
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin && (
          <StatCard
            title="Volontari Totali"
            value={stats.totalVolontari}
            icon={UsersIcon}
            color="blue"
            onClick={() => navigate("/volontari")}
          />
        )}
        <StatCard
          title="Postazioni Attive"
          value={stats.postazioniAttive}
          icon={MapPinIcon}
          color="green"
          onClick={() => navigate("/volontari/disponibilita")}
        />
        <StatCard
          title="Turni Questa Settimana"
          value={stats.turniSettimana}
          icon={CalendarIcon}
          color="purple"
          href="#miei-prossimi-turni"
        />
        {isAdmin && (
          <StatCard
            title="Turni Incompleti"
            value={stats.turniIncompleti}
            icon={ExclamationTriangleIcon}
            color="red"
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prossimi Turni */}
        {showMieiProssimiTurni && (
          <div
            id="miei-prossimi-turni"
            className="bg-white rounded-lg shadow-sm border border-gray-200"
          >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-primary-600" />I Miei
              Prossimi Turni
            </h3>
          </div>
          <div className="accent-line mx-6" />
          <div className="p-6 pt-5">
            {stats.prossimiTurni.length > 0 ? (
              <div className="space-y-4">
                {stats.prossimiTurni.map((turno) => (
                  <div
                    key={turno.id}
                    className="relative border border-gray-200 rounded-lg p-4 pl-6 hover:bg-gray-50 transition-colors"
                  >
                    <span
                      className="accent-bar top-4 bottom-4"
                      aria-hidden="true"
                    />
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {turno.luogo}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatDate(turno.data)}
                        </p>
                        {turno.indirizzo && (
                          <p className="text-xs text-gray-500">
                            {turno.indirizzo}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {turno.orario}
                        </span>
                        <button
                          type="button"
                          onClick={() => openTurnDetails(turno)}
                          className="h-7 w-7 rounded-full border border-primary-200 text-primary-700 text-xs font-bold flex items-center justify-center hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                          title="Vedi chi è prima e dopo di te"
                          aria-label="Dettagli turni della giornata"
                        >
                          ?
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Stato:</span>{" "}
                      <span
                        className={`capitalize ${
                          turno.stato === "assegnato"
                            ? "text-blue-600"
                            : turno.stato === "completato"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {turno.stato}
                      </span>
                    </div>
                    {turno.volontari.length > 0 && (
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Con:</span>{" "}
                        {turno.volontari.join(", ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  Nessun turno assegnato
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  I tuoi turni futuri confermati appariranno qui
                </p>
              </div>
            )}
            <CalendarSyncCard />
          </div>
          </div>
        )}

        {/* Incarichi (pulizie) */}
        {showIncarichi && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <UsersIcon className="h-5 w-5 mr-2 text-primary-600" />
              Incarichi
            </h3>
          </div>
          <div className="accent-line mx-6" />
          <div className="p-6 pt-5">
            {incarichiPulizieGrouped && incarichiPulizieGrouped.length > 0 ? (
              <div className="space-y-4">
                {incarichiPulizieGrouped.map((inc, idx) => {
                  return (
                    <div
                      key={`${inc.settimana || idx}`}
                      className="relative border border-gray-200 rounded-lg p-4 pl-6 flex flex-col gap-3 transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm"
                    >
                      <span
                        className="accent-bar top-4 bottom-4"
                        aria-hidden="true"
                      />
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-lg">
                            🧹
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Turno pulizie
                            </p>
                            <p className="text-sm text-gray-700 leading-tight">
                              {formatWeekRange(inc.settimana, inc.settimana_fine)}
                            </p>
                          </div>
                        </div>
                        <span className="self-start sm:self-auto inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          Settimana
                        </span>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
                        {inc.items
                          .slice()
                          .sort((a, b) => {
                            const order = { infrasettimanale: 0, fine_settimana: 1 };
                            return (order[a.slot] ?? 2) - (order[b.slot] ?? 2);
                          })
                          .map((item, i) => (
                            <div
                              key={`${item.slot}_${i}`}
                              className="flex items-start gap-3 text-sm text-gray-800"
                            >
                              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mt-0.5">
                                {item.slot?.startsWith("fine") ? "FS" : "INF"}
                              </span>
                              <div className="leading-snug">
                                <p className="font-medium text-gray-900">
                                  {capitalizeFirst(item.slot || "")}
                                </p>
                                {Array.isArray(item.groups) &&
                                  item.groups.length > 0 && (
                                    <div className="text-gray-700 text-sm">
                                      {/* Mobile: gruppi uno per riga */}
                                      <div className="md:hidden space-y-1">
                                        {item.groups
                                          .map((g) => g.group_nome)
                                          .filter(Boolean)
                                          .map((name, idx) => (
                                            <p key={`${item.slot}_g_${idx}`}>
                                              Gruppo: {name}
                                            </p>
                                          ))}
                                      </div>
                                      {/* Desktop: gruppi in linea */}
                                      <div className="hidden md:block">
                                        Gruppo:{" "}
                                        {item.groups
                                          .map((g) => g.group_nome)
                                          .filter(Boolean)
                                          .join(", ")}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <CalendarIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  Nessun incarico di pulizie nel periodo selezionato
                </p>
              </div>
            )}
          </div>
          </div>
        )}

        {showProssimeAdunanze && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <img
                  src={conduttoreIcon}
                  alt="Conduttore"
                  className="h-6 w-6 mr-2 object-contain"
                />
                Prossime Adunanze da condurre
              </h3>
            </div>
            <div className="accent-line mx-6" />
            <div className="p-6 pt-5">
              {stats.prossimeAdunanze && stats.prossimeAdunanze.length > 0 ? (
                <div className="space-y-4">
                  {stats.prossimeAdunanze.map((adunanza, index) => {
                    const indirizzo = adunanza.indirizzo?.trim();
                    const luogoFallback =
                      adunanza.luogo || adunanza.titolo || "—";
                    const luogoDisplay = indirizzo || luogoFallback;

                    return (
                      <div
                        key={`${adunanza.adunanza_id}_${adunanza.data_occurrence}_${index}`}
                        className="relative border border-gray-200 rounded-lg p-4 pl-6 transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm"
                      >
                        <span
                          className="accent-bar top-4 bottom-4"
                          aria-hidden="true"
                        />
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">
                              <span className="font-medium text-gray-700">
                                Conduttore:
                              </span>{" "}
                              <span className="text-gray-700">
                                {conduttoreName || "—"}
                              </span>
                            </p>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {adunanza.titolo || adunanza.luogo || "Adunanza"}
                            </h4>
                            <p className="text-sm text-gray-600">
                            {formatAdunanzaDate(adunanza.data_occurrence)}
                          </p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {adunanza.ora_inizio
                              ? adunanza.ora_inizio.substring(0, 5)
                              : "—"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 mt-2 flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4 text-primary-500" />
                          <span className="font-medium">Luogo:</span>{" "}
                          <span>{luogoDisplay}</span>
                        </div>
                        {adunanza.note && (
                          <p className="text-xs text-gray-500 mt-1">
                            {adunanza.note}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">
                    Nessuna adunanza in programma come conduttore
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Quando verrai assegnato a un'adunanza per il servizio
                    apparirà qui
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bacheca */}
        {showBacheca && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
                Bacheca
              </h3>
            </div>
            <div className="accent-line mx-6" />
            <div className="p-6 pt-5">
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {bachecaTabs.map((tab) => {
                    const isActive = bachecaActiveTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setBachecaActiveTab(tab.key)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                          isActive
                            ? "border-primary-300 bg-primary-100 text-primary-800"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {bachecaActiveTab === BACHECA_TAB_KEYS.EVENTI ? (
                  bachecaEventsLoading ? (
                    <div className="flex items-center justify-center py-6 space-x-2 text-primary-700">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                      <span className="text-sm">Caricamento eventi...</span>
                    </div>
                  ) : bachecaEventsError ? (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                      {bachecaEventsError}
                    </div>
                  ) : bachecaEvents.length > 0 ? (
                    <div className="space-y-3">
                      {bachecaEvents.map((ev) => {
                        const expanded = expandedEventId === ev.id;
                        return (
                          <div
                            key={ev.id}
                            className="relative border border-gray-200 rounded-lg"
                          >
                            <span
                              className="accent-bar top-3 bottom-3"
                              aria-hidden="true"
                            />
                            <button
                              type="button"
                              className="w-full text-left pl-6 pr-4 py-3 flex items-start justify-between hover:bg-gray-50"
                              onClick={() =>
                                setExpandedEventId((prev) =>
                                  prev === ev.id ? null : ev.id
                                )
                              }
                            >
                              <div>
                                <p className="text-base font-semibold text-gray-900">
                                  {ev.nome}
                                </p>
                                <p className="text-sm text-gray-700 mt-1">
                                  {formatRange(ev.data_inizio, ev.data_fine)}
                                </p>
                              </div>
                              <span className="text-xs text-primary-700 pl-3">
                                {expanded ? "Nascondi" : "Dettagli"}
                              </span>
                            </button>
                            {expanded && (
                              <div className="pl-6 pr-4 py-3 border-t border-gray-200 bg-gray-50">
                                {ev.note ? (
                                  <p className="text-sm text-gray-700 whitespace-pre-line">
                                    {ev.note}
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-500">
                                    Nessuna nota.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Nessun evento in bacheca
                    </p>
                  )
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <button
                        type="button"
                        onClick={() =>
                          setBachecaDayOffset((prev) => Math.max(0, prev - 1))
                        }
                        disabled={bachecaDayOffset <= 0}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          bachecaDayOffset <= 0
                            ? "Sei già alla data odierna"
                            : "Vai al giorno precedente"
                        }
                        aria-label="Vai al giorno precedente"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <div className="text-center px-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Data selezionata
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {bachecaSelectedDate
                            ? formatDate(formatDateParam(bachecaSelectedDate))
                            : "Data non disponibile"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setBachecaDayOffset((prev) =>
                            Math.min(BACHECA_MAX_FORWARD_DAYS, prev + 1)
                          )
                        }
                        disabled={bachecaDayOffset >= BACHECA_MAX_FORWARD_DAYS}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          bachecaDayOffset >= BACHECA_MAX_FORWARD_DAYS
                            ? `Hai raggiunto il limite massimo (+${BACHECA_MAX_FORWARD_DAYS} giorni)`
                            : "Vai al giorno successivo"
                        }
                        aria-label="Vai al giorno successivo"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {dailyPostazioniLoading ? (
                      <div className="flex items-center justify-center py-6 space-x-2 text-primary-700">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                        <span className="text-sm">
                          Caricamento turni giornalieri...
                        </span>
                      </div>
                    ) : dailyPostazioniError ? (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                        {dailyPostazioniError}
                      </div>
                    ) : dailyPostazioniTurni.length > 0 ? (
                      <div className="space-y-3">
                        {dailyPostazioniTurni.map((postazione) => (
                          <div
                            key={postazione.id || postazione.luogo}
                            className="relative border border-gray-200 rounded-lg p-4 pl-6"
                          >
                            <span
                              className="accent-bar top-3 bottom-3"
                              aria-hidden="true"
                            />
                            <h4 className="text-base font-semibold text-gray-900">
                              {postazione.luogo}
                            </h4>
                            {postazione.indirizzo && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {postazione.indirizzo}
                              </p>
                            )}

                            <div className="mt-3 space-y-3">
                              {postazione.slots.map((slot, index) => (
                                <div
                                  key={`${postazione.id || postazione.luogo}-${slot.id || slot.orarioInizio || index}`}
                                  className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0"
                                >
                                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                    {slot.orarioInizio && slot.orarioFine
                                      ? `${slot.orarioInizio} - ${slot.orarioFine}`
                                      : "Orario non disponibile"}
                                  </p>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {slot.volontari.map((name) => (
                                      <span
                                        key={`${postazione.id || postazione.luogo}-${slot.id || slot.orarioInizio}-${name}`}
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                                      >
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        Nessuna postazione con turni assegnati per questa data
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifiche */}
        {showNotificheRecenti && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-yellow-600" />
              Notifiche Recenti
            </h3>
          </div>
          <div className="p-6">
            {stats.notifiche.length > 0 ? (
              <div className="space-y-3">
                {stats.notifiche.map((notifica) => (
                  <div key={notifica.id} className="flex items-start space-x-3">
                    <div
                      className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                        notifica.tipo === "warning"
                          ? "bg-yellow-400"
                          : "bg-blue-400"
                      }`}
                    />
                    <p className="text-sm text-gray-700">
                      {notifica.messaggio}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">Nessuna notifica</p>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Azioni Rapide */}
      {user?.ruolo === "admin" || user?.ruolo === "super_admin" ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Azioni Rapide
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              className="btn-primary"
              onClick={() => navigate("/turni/gestione")}
            >
              Assegna turni automaticamente
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate("/postazioni/gestione")}
            >
              Gestisci postazioni
            </button>
            <button
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50"
              onClick={() => navigate("/volontari/disponibilita")}
            >
              Inserisci disponibilità
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Azioni Rapide
          </h3>
          <button
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50"
            onClick={() => navigate("/volontari/disponibilita")}
          >
            Inserisci disponibilità
          </button>
        </div>
      )}

      <PublicTalksUpcomingCard
        disabled={Boolean(activeCongregazione?.servizio_speciale)}
      />

      {/* News */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <NewspaperIcon className="h-5 w-5 mr-2 text-primary-600" />
            News di Sistema
          </h3>
          <button
            type="button"
            onClick={() => setNewsExpanded((prev) => !prev)}
            className="inline-flex items-center px-3 py-1.5 border border-primary-200 text-primary-700 rounded-md text-sm hover:bg-primary-50"
          >
            {newsExpanded ? "Comprimi" : "Espandi"}
          </button>
        </div>
        <div className="accent-line mb-5" />
        {!newsExpanded ? (
          <div className="text-sm text-gray-600 flex items-center justify-between">
            <span>
              {totalNewsCount > 0
                ? `${totalNewsCount} news disponibili`
                : "Nessuna news disponibile"}
            </span>
          </div>
        ) : newsLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
            <span className="text-gray-600">Caricamento news...</span>
          </div>
        ) : newsError ? (
          <div className="text-center text-sm text-red-600 py-6">
            {newsError}
          </div>
        ) : newsItems.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            Nessuna news disponibile
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-sm mb-4">
              <button
                type="button"
                onClick={() => setNewsPage((prev) => Math.max(1, prev - 1))}
                disabled={!canGoPrev}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                ← Precedenti
              </button>
              <span className="text-gray-600">
                Pagina {displayTotalPages > 0 ? displayCurrentPage : 0} di{" "}
                {displayTotalPages}
              </span>
              <button
                type="button"
                onClick={() => setNewsPage((prev) => prev + 1)}
                disabled={!canGoNext}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successive →
              </button>
            </div>
            <div className="space-y-4">
              {newsItems.map((item) => (
                <div
                  key={item.id}
                  className="relative border border-gray-200 rounded-lg p-4 pl-6 transition hover:bg-gray-50 hover:border-primary-200 hover:shadow-sm"
                >
                  <span
                    className="accent-bar top-4 bottom-4"
                    aria-hidden="true"
                  />
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-base font-semibold text-gray-900">
                      {item.titolo}
                    </h4>
                    <span className="text-xs text-gray-500">
                      {formatNewsDate(item.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {item.author_nome || item.author_email || "Team Planner Turni"}
                  </p>
                  <p className="text-sm text-gray-700">
                    {summarizeNewsContent(item.contenuto)}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal impostazioni dashboard */}
      {dashboardSettingsOpen && canManageDashboardSections && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">Dashboard</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  Visibilità Sezioni
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Valide per tutta la congregazione selezionata.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDashboardSettingsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Chiudi impostazioni dashboard"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-3">
              {dashboardSectionOptions.map((section) => (
                <label
                  key={section.key}
                  className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg p-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {section.label}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={Boolean(dashboardSettingsDraft[section.key])}
                    onChange={() => toggleDashboardSection(section.key)}
                    className="mt-1 h-4 w-4"
                  />
                </label>
              ))}
              {!hasAtLeastOneSectionEnabled && (
                <p className="text-xs text-red-600">
                  Mantieni almeno una sezione attiva.
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDashboardSettingsOpen(false)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={saveDashboardSectionsSettings}
                disabled={dashboardSettingsSaving}
                className="px-3 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {dashboardSettingsSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal turni della giornata */}
      {turnDetails.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">Turni della giornata</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {turnDetails.meta?.luogo || "Postazione"}
                </h3>
                {turnDetails.meta?.data && (
                  <p className="text-xs text-gray-500">
                    {formatDate(turnDetails.meta.data)}
                  </p>
                )}
              </div>
              <button
                onClick={closeTurnDetails}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Chiudi dettagli turni"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              {turnDetails.loading ? (
                <div className="flex items-center justify-center py-6 space-x-2 text-primary-700">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span>Caricamento turni della giornata...</span>
                </div>
              ) : turnDetails.error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                  {turnDetails.error}
                </div>
              ) : turnDetails.turns.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600">
                    Vedi chi è prima o dopo di te alla stessa postazione nella giornata.
                  </p>
                  {(() => {
                    const selectedIndex = turnDetails.turns.findIndex(
                      (t) => t.id === turnDetails.selectedTurnId
                    );
                    return turnDetails.turns.map((t, idx) => {
                      const isCurrent = idx === selectedIndex;
                      const label = isCurrent
                        ? "Il tuo turno"
                        : idx < selectedIndex
                        ? "Prima"
                        : "Dopo";
                      return (
                        <div
                          key={`${t.id}_${idx}`}
                          className={`border rounded-lg p-3 ${
                            isCurrent
                              ? "border-primary-200 bg-primary-50"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {t.orarioInizio && t.orarioFine
                                  ? `${t.orarioInizio} - ${t.orarioFine}`
                                  : t.orarioInizio || t.orarioFine || "Orario non indicato"}
                              </p>
                              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
                                {t.volontari && t.volontari.length > 0 ? (
                                  t.volontari.map((volontario) => {
                                    const phoneHref = buildPhoneHref(
                                      volontario.telefono
                                    );
                                    const label = volontario.telefono
                                      ? `${volontario.nomeCompleto} • ${formatPhone(
                                          volontario.telefono
                                        )}`
                                      : volontario.nomeCompleto;

                                    if (!phoneHref) {
                                      return (
                                        <span key={volontario.id}>{label}</span>
                                      );
                                    }

                                    return (
                                      <a
                                        key={volontario.id}
                                        href={phoneHref}
                                        className="text-primary-700 hover:text-primary-800 hover:underline"
                                      >
                                        {label}
                                      </a>
                                    );
                                  })
                                ) : (
                                  <span>Proclamatori non indicati</span>
                                )}
                              </div>
                            </div>
                            <span
                              className={`text-xs font-medium px-2 py-1 rounded-full ${
                                isCurrent
                                  ? "bg-primary-100 text-primary-800"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  Nessun turno trovato per questa giornata alla postazione.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
